import React, { Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { PAGE_CATEGORIES, PAGE_STEPS } from "../constants/products";
import GlobalStore from "@ipru-common/store/GlobalStore";
import {
  APPLICATION_ONE,
  APPLICATION_TWO,
  SavingsAppFormSteps,
  SPOUSE_COVER,
} from "@ipru-common/constants/SiteConstants";
import { OPTION_VALUES } from "@ipru-common/constants/InputValues";
import { getCurrentProductFromMeta } from "@ipru-common/utils/CommonFunctions";
import { isEmpty } from "lodash";
import { updateUserInput } from "@ipru-common/utils/UserInputMapper/CommonUserInput";
import _ from "lodash";
import { GlobalContext as GlobalContextProvider } from "shared-library";
import { DataLayerProvider } from "./DataLayerContext";
import { HotJarProvider } from "./HotJarContext";
import usePostFeedBackService from "@ipru-common/libs/usePostFeedbackService";

export const defaultAppFormData = {
  isMwpa: false,
  kyc: false,
  kycType: OPTION_VALUES.PENDING,
  aadhaarDetail: null,
  panDetail: null,
  aadhaarNumber: "",
  panCardNumber: "",
  formWizard: {
    personalDetails: null,
    occupationDetails: null,
    nomineeDetails: null,
    healthDetails: null,
    // personalHealth: null,
  },
  stepsCleared: {
    personalDetails: false,
    occupationDetails: false,
    nomineeDetails: false,
    healthDetails: false,
    // personalHealth: false,
  },

  isReverifyingEkyc: null,
  lastQuetionAns: "",
  lastQuetionState: "",
  lastSection: "",
  transactionId: "",
  appNO: "",
  generatingAppNumber: false,
};

export const GlobalProvider = ({ children, pageModelPath, defaultGlobalState, defaultGlobalFields = {} }) => {
  const globalValues = useContext(GlobalContextProvider)?.global;

  const [global, setGlobal] = useState(
    /** @type {GlobalState} */ (
      defaultGlobalState ||
        globalValues || {
          is_nri: false,
          currentProductCode: "",
          currentProduct: getCurrentProductFromMeta() || "",
          currentPageCategory: PAGE_CATEGORIES.calculator,
          currentPageStep: PAGE_STEPS.calculator,
          customizePlan: false,
          resumeDropOffJourney: null,
          calculatorFormData: {},
          quoteFormData: {},
          appFormData: defaultAppFormData,
          spouseCover: OPTION_VALUES.FALSE,
          wifeDataConsent: OPTION_VALUES.FALSE,
          [APPLICATION_TWO]: {
            appFormData: defaultAppFormData,
            calculatorFormData: {},
            quoteFormData: {},
            resumeDropOffJourney: null,
          },
          suitabilityMatrix: {
            financialAndFamilyGoals: "",
            goalHorizon: "",
            income: "",
            insurancePortfolioHeld: "",
            isSuitabilityReq: "N",
            lifeStage: "",
            paymentPreference: "",
            riskProfile: "",
          },
          currentApplication: APPLICATION_ONE,
          savingForm: {
            currentStep: SavingsAppFormSteps.ADHAR_DETAILS,
          },
          otcAppFormData: {},
          lastQuetionAns: "ekycpage",
          lastQuetionState: "new",
          lastSection: "ekycpage",
          existingCustDetails: null,
          landingPage: {
            claimCorner: {
              title: "",
              modelData: {},
            },
          },
          callDropOff: null,
          isQuoteFormInitialized: false,
          pageModelPath,
          UID: "",
          blacklistedContacts: {
            mobile: [],
            email: [],
          },
          landingQuoteApiData: [],
          ...defaultGlobalFields,
        }
    )
  );

  const { submitFeedback } = usePostFeedBackService();

  const callFeedbackApi = (data) => {
    submitFeedback(data);
  };

  const updateAppFormDataWithSteps = useCallback((key, data, otherData = {}, stepsCleared) => {
    setGlobal((prevGlobal) => ({
      ...prevGlobal,
      appFormData: {
        ...prevGlobal.appFormData,
        formWizard: {
          ...prevGlobal.appFormData.formWizard,
          [key]: { ...data },
        },
        stepsCleared: {
          ...prevGlobal.appFormData.stepsCleared,
          [key]: stepsCleared,
        },
      },
      ...otherData,
    }));
    if (key !== "lastChangedField")
      setTimeout(() => {
        updateUserInput(callFeedbackApi);
      }, [500]);
  }, []);
  const updateAppFormData = useCallback((key, data, otherData = {}) => {
    setGlobal((prevGlobal) => ({
      ...prevGlobal,
      appFormData: {
        ...prevGlobal.appFormData,
        formWizard: {
          ...prevGlobal.appFormData.formWizard,
          [key]: { ...data },
        },
        stepsCleared: {
          ...prevGlobal.appFormData.stepsCleared,
          [key]: true,
        },
      },
      ...otherData,
    }));
    if (key !== "lastChangedField")
      setTimeout(() => {
        updateUserInput(callFeedbackApi);
      }, [500]);
  }, []);

  const submitCalculatorFormRef = useRef(null);
  const partialSubmitCalculatorFormRef = useRef(null);

  const viewBenefitRef = useRef(null);

  const globalRef = useRef(global);

  useEffect(() => {
    globalRef.current = global;
    GlobalStore.setSnapshot(global);
  }, [global]);

  useEffect(() => {
    if (!isEmpty(global?.quoteFormData)) {
      updateUserInput(callFeedbackApi);
    }
  }, [global?.existingCustomer?.clientId, global?.appFormData?.appNO]);

  const isCustomerNri = useMemo(
    () => global?.calculatorFormData?.nri === OPTION_VALUES.NRI_YES,
    [global?.calculatorFormData?.nri]
  );

  const spouseCoverIncluded = !_.isEmpty(global?.[SPOUSE_COVER]?.quoteFormData, true);

  const updateGlobalValue = useCallback((key, value) => {
    setGlobal((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  /** @type {GlobalContext} */
  const values = {
    global,
    setGlobal,
    updateGlobalValue,
    submitCalculatorFormRef,
    viewBenefitRef,
    updateAppFormData,
    isCustomerNri,
    spouseCoverIncluded,
    updateAppFormDataWithSteps,
    partialSubmitCalculatorFormRef,
  };

  return (
    <GlobalContextProvider.Provider value={useMemo(() => values, [values])}>
      <DataLayerProvider globalState={global}>
        <HotJarProvider>
          <Suspense fallback={<div></div>}>{children}</Suspense>
        </HotJarProvider>
      </DataLayerProvider>
    </GlobalContextProvider.Provider>
  );
};

/**
 *
 * @returns {GlobalContext}
 */
export const useGlobalContext = (addOnKey) => {
  const context = useContext(GlobalContextProvider);
  if (!context) {
    throw new Error("useGlobalContext must be used within a GlobalProvider");
  }

  if (addOnKey && context?.global) {
    return getSpouseCalculator(context);
  }

  return context;
};

const getSpouseCalculator = (context) => {
  const {
    calculatorFormData,
    quoteFormData,
    SPOUSE_COVER: { calculatorFormData: spouseCalculator, ...spouseData },
  } = context.global;
  const { MALE, FEMALE } = OPTION_VALUES.GENDER;
  const gender = calculatorFormData?.gender === MALE ? FEMALE : MALE;

  const spouseCalculatedSubmitted = !_.isEmpty(spouseCalculator, true);

  const spouseDefaultCalculator = {
    ...calculatorFormData,
    gender,
    emailid: "",
    mobileno: "",
    invalid: !spouseCalculatedSubmitted,
  };

  const newData = {
    ...context,
    global: {
      ...context?.global,
      ...spouseData,
      calculatorFormData: spouseCalculatedSubmitted ? spouseCalculator : { ...spouseDefaultCalculator },
      husband: { calculator: calculatorFormData, quote: quoteFormData },
    },
  };
  return newData;
};
